import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Alert, AlertTitle, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { reportClientError } from '../utils/telemetry';

// Catches render-time errors so a crash in one view shows a recoverable
// fallback instead of unmounting the whole app into a white screen.

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Where this boundary sits, included in the error report (e.g. "route:/batch-plan") */
  scope?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    reportClientError(error, {
      scope: this.props.scope || 'app',
      componentStack: (errorInfo.componentStack || '').slice(0, 2000),
    });
  }

  handleTryAgain = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <Alert severity="error" sx={{ maxWidth: 600, width: '100%' }}>
            <AlertTitle>Something went wrong</AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              An unexpected error occurred in this view. It has been reported automatically.
              You can try again, or reload the app if the problem persists.
            </Typography>
            <Typography variant="caption" component="pre" sx={{ mb: 2, whiteSpace: 'pre-wrap', opacity: 0.7 }}>
              {this.state.error.message}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" size="small" onClick={this.handleTryAgain}>
                Try again
              </Button>
              <Button variant="contained" size="small" startIcon={<RefreshIcon />} onClick={this.handleReload}>
                Reload
              </Button>
            </Box>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}
